<template>
    <BaseDialog v-model:open="open" title="Action details" show-close float-close dialog-content-class="p-5 sm:p-6 md:!max-w-xl">
        <div class="flex flex-col gap-6">
            <div class="flex min-w-0 flex-col gap-1 pr-8">
                <span class="details font-semibold text-valence-grey-700">{{ channelPresentation.label }}</span>
                <h2 class="body text-valence-grey-850">{{ action.title }}</h2>
                <span class="journeys-action-detail-tag" :class="actionMode.className">
                    <i class="bi text-[10px]" :class="actionMode.icon" aria-hidden="true"></i>
                    {{ actionMode.label }}
                </span>
            </div>

            <section v-if="action.details" class="flex flex-col gap-1.5">
                <h3 class="details font-semibold text-valence-grey-850">Details</h3>
                <p class="body-small text-valence-grey-750">{{ action.details }}</p>
            </section>

            <section v-if="action.justification" class="flex flex-col gap-1.5">
                <h3 class="details font-semibold text-valence-grey-850">Why Nadia suggested this</h3>
                <p class="body-small text-valence-grey-750">{{ action.justification }}</p>
            </section>

            <section v-if="action.evidence_groups?.length" class="flex flex-col gap-2">
                <h3 class="details font-semibold text-valence-grey-850">Based on</h3>
                <InsightEvidenceChips :evidence-groups="action.evidence_groups ?? []" :show-label="false" />
            </section>

            <section v-if="action.development_goal" class="flex flex-col gap-2">
                <h3 class="details font-semibold text-valence-grey-850">Supporting context</h3>
                <div class="flex flex-wrap gap-2">
                    <SourceChip v-if="action.development_goal" source="journeys" size="xs" class="max-w-full">
                        <span class="min-w-0 truncate">{{ action.development_goal.title }}</span>
                    </SourceChip>
                </div>
            </section>

            <p v-if="!action.details && !action.justification && !action.development_goal && !action.evidence_groups?.length" class="body-small text-valence-grey-750">
                No additional details are available for this action yet.
            </p>

            <p
                v-if="conversationError"
                data-testid="weekly-action-conversation-error"
                role="alert"
                class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 body-small text-red-800">
                {{ conversationError }}
            </p>

            <div v-if="showPrimaryAction && primaryAction && action.status !== 'completed'" class="flex justify-end">
                <a
                    v-if="primaryAction.kind === 'link'"
                    data-testid="weekly-action-details-resource-link"
                    :href="primaryAction.href"
                    :target="primaryAction.target"
                    :rel="primaryAction.rel"
                    class="inline-flex items-center justify-center rounded-lg bg-valence-primary-500 px-4 py-2 body-small font-medium text-white transition-colors hover:bg-valence-primary-600"
                    @click="handlePrimaryActionLinkClick">
                    {{ primaryAction.ctaLabel }}
                </a>
                <button
                    v-else
                    data-testid="weekly-action-details-start-conversation"
                    type="button"
                    class="inline-flex items-center justify-center rounded-lg bg-valence-primary-500 px-4 py-2 body-small font-medium text-white transition-colors hover:bg-valence-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                    :disabled="isConversationLaunching"
                    :aria-busy="isConversationLaunching"
                    @click="startConversation">
                    {{ primaryAction.ctaLabel }}
                </button>
            </div>
        </div>
    </BaseDialog>
</template>

<script setup lang="ts">
import BaseDialog from "~vue/components/BaseDialog.vue";
import SourceChip from "~vue/components/SourceChip.vue";
import InsightEvidenceChips from "~vue/Labs/InsightEvidenceChips.vue";
import { computed } from "vue";

import { getWeeklyActionChannelPresentation, getWeeklyActionModePresentation } from "./weeklyActionPresentation";
import { getWeeklyActionPrimaryAction, handleWeeklyActionPrimaryLinkClick, logWeeklyActionPrimaryActionClicked } from "./weeklyActionPrimaryAction";
import type { WeeklyAction } from "./weeklyActions";

const props = defineProps<{
    action: WeeklyAction;
    rolePlaySetupUrl: string;
    showPrimaryAction: boolean;
    isConversationLaunching?: boolean;
    conversationError?: string | null;
}>();

const emit = defineEmits<{
    "start-conversation": [initialUserMessage: string];
}>();

const open = defineModel<boolean>("open", { default: false });
const channelPresentation = computed(() => getWeeklyActionChannelPresentation(props.action.channel));
const actionMode = computed(() => getWeeklyActionModePresentation(props.action.channel));
const primaryAction = computed(() => getWeeklyActionPrimaryAction(props.action, props.rolePlaySetupUrl));

function handlePrimaryActionLinkClick(event: MouseEvent) {
    const action = primaryAction.value;
    if (action?.kind === "link") {
        void handleWeeklyActionPrimaryLinkClick(action, event);
    }
}

function startConversation() {
    if (props.isConversationLaunching) {
        return;
    }
    const action = primaryAction.value;
    if (action?.kind !== "start_conversation") {
        return;
    }
    logWeeklyActionPrimaryActionClicked(action);
    emit("start-conversation", action.initialUserMessage);
}
</script>

<style scoped>
.journeys-action-detail-tag {
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    gap: 4px;
    height: 20px;
    padding: 1px 10px;
    border-radius: 9999px;
    color: #2a2723;
    font-family: var(--font-scaffold, "Space Grotesk", sans-serif);
    font-size: 11px;
    line-height: 18px;
}

.journeys-action-card__tag--own {
    background: #f7f5f2;
}

.journeys-action-card__tag--nadia {
    background: #c6e9e9;
    color: #1e6461;
}
</style>
